import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Layers, Loader2, FileText, Image as ImageIcon, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMatchingExplodedViews, getExplodedViewSignedUrl, type ExplodedView } from "@/hooks/useExplodedViews";
import { toast } from "sonner";

interface Props {
  brand?: string | null;
  applianceType?: string | null;
  modelNumber?: string | null;
  /** When set, views with this workflow_item_id are highlighted and floated to the top. */
  currentWorkflowItemId?: string | null;
  defaultOpen?: boolean;
  /** When true, only views with matching brand AND model are shown (no generic fallbacks). */
  strictModelMatch?: boolean;
}

export function ExplodedViewsPanel({
  brand,
  applianceType,
  modelNumber,
  currentWorkflowItemId,
  defaultOpen = false,
  strictModelMatch = false,
}: Props) {
  const { data, isLoading } = useMatchingExplodedViews(brand, applianceType, modelNumber, {
    requireModelMatch: strictModelMatch,
  });
  const [open, setOpen] = useState(defaultOpen);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewView, setPreviewView] = useState<ExplodedView | null>(null);

  const views = (data ?? []).slice().sort((a, b) => {
    const aMatch = currentWorkflowItemId && a.workflow_item_id === currentWorkflowItemId ? 1 : 0;
    const bMatch = currentWorkflowItemId && b.workflow_item_id === currentWorkflowItemId ? 1 : 0;
    return bMatch - aMatch;
  });

  // Auto-open when there's a step-linked view
  useEffect(() => {
    if (currentWorkflowItemId && views.some((v) => v.workflow_item_id === currentWorkflowItemId)) {
      setOpen(true);
    }
  }, [currentWorkflowItemId, views]);

  const handleOpen = async (v: ExplodedView) => {
    const url = await getExplodedViewSignedUrl(v.file_path);
    if (!url) {
      toast.error("Could not open exploded view");
      return;
    }
    if (v.file_type === "pdf") {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      setPreviewView(v);
      setPreviewUrl(url);
    }
  };

  if (!isLoading && views.length === 0) return null;

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Layers className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-foreground text-sm">Exploded Views</p>
            <p className="text-xs text-muted-foreground">
              {isLoading ? "Searching..." : `${views.length} matching diagram${views.length === 1 ? "" : "s"}`}
              {brand ? ` · ${brand}` : ""}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            views.map((v) => {
              const isStepMatch =
                currentWorkflowItemId && v.workflow_item_id === currentWorkflowItemId;
              return (
                <div
                  key={v.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${
                    isStepMatch ? "border-primary bg-primary/5" : "border-border bg-background"
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    {v.file_type === "pdf" ? (
                      <FileText className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground truncate">
                        {v.title || v.original_filename || "Untitled view"}
                      </p>
                      {isStepMatch && (
                        <Badge className="gap-1">
                          <Sparkles className="w-3 h-3" />
                          For this step
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {v.brand && <Badge variant="secondary" className="text-xs">{v.brand}</Badge>}
                      {v.appliance_type && (
                        <Badge variant="outline" className="text-xs">{v.appliance_type}</Badge>
                      )}
                      {v.models?.slice(0, 3).map((m) => (
                        <Badge key={m} variant="outline" className="text-xs">{m}</Badge>
                      ))}
                      {v.models && v.models.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{v.models.length - 3}</Badge>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleOpen(v)} title="Open">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      )}

      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o) { setPreviewUrl(null); setPreviewView(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewView?.title || previewView?.original_filename || "Exploded view"}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="w-full max-h-[75vh] overflow-auto bg-muted rounded-lg flex items-center justify-center">
              <img src={previewUrl} alt={previewView?.title || "Exploded view"} className="max-w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
